import React, { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  X as XIcon, Phone, Mail, MapPin, Tag, ShieldCheck, ShieldAlert, Calendar,
  Plus, FileText, Loader2, StickyNote, Download,
} from "lucide-react";
import { serviceLabel } from "@/components/NewVisitModal";
import { StatusBadge } from "@/components/VisitsHistoryTable";

const idParams = (p) => ({
  fiscal_code: p.patient_fiscal_code || undefined,
  name: p.patient_name || undefined,
  surname: p.patient_surname || undefined,
  birth: p.patient_birth || undefined,
});

// Stesso formato usato da PatientConsentBlock (consensi v2)
const consentKey = (p) => {
  if (p.patient_fiscal_code) return `cf:${p.patient_fiscal_code.toUpperCase()}`;
  if (p.patient_name && p.patient_surname) {
    return `nb:${p.patient_name.trim().toLowerCase()}|${p.patient_surname.trim().toLowerCase()}|${(p.patient_birth || "").trim()}`;
  }
  return null;
};

function Section({ title, icon: Icon, children, testid }) {
  return (
    <section className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid={testid}>
      <h4 className="text-xs font-extrabold uppercase tracking-wider text-brand-navy flex items-center gap-1.5 mb-3"><Icon size={13} className="text-brand-azure" /> {title}</h4>
      {children}
    </section>
  );
}

function TagsSection({ patient }) {
  const [tags, setTags] = useState(null);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/medico/patient-tags", { params: idParams(patient) })
      .then(({ data }) => setTags(data.tags || []))
      .catch(() => setTags([]));
  }, [patient]);

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put("/medico/patient-tags", { ...idParams(patient), tags: next });
      setTags(data.tags || []);
    } catch (e) { toast.error(apiErr(e, "Errore salvataggio tag")); }
    finally { setSaving(false); }
  };

  const add = () => {
    const t = input.trim();
    if (!t) return;
    setInput("");
    save([...(tags || []), t]);
  };

  return (
    <Section title="Tag clinici / organizzativi" icon={Tag} testid="pp-tags">
      {tags === null ? <div className="text-xs text-slate-400">Caricamento…</div> : (
        <>
          <div className="flex flex-wrap gap-1.5 mb-2" data-testid="pp-tags-list">
            {tags.length === 0 && <span className="text-xs text-slate-400">Nessun tag</span>}
            {tags.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 bg-brand-azure/10 text-brand-azure text-xs font-bold px-2 py-1 rounded-full" data-testid={`pp-tag-${t.replace("#", "")}`}>
                {t}
                <button onClick={() => save(tags.filter((x) => x !== t))} disabled={saving} className="hover:text-red-600" data-testid={`pp-tag-remove-${t.replace("#", "")}`}><XIcon size={11} /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input data-testid="pp-tag-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="#glaucoma, #controllo-annuale…" className="flex-1 px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-sm focus:outline-none focus:border-brand-azure" />
            <button data-testid="pp-tag-add" onClick={add} disabled={saving || !input.trim()} className="bg-brand-navy text-white text-xs font-bold px-3 py-1.5 rounded-lg disabled:opacity-50">Aggiungi</button>
          </div>
        </>
      )}
    </Section>
  );
}

function ConsentSection({ patient }) {
  const [consent, setConsent] = useState(null);
  const key = consentKey(patient);

  useEffect(() => {
    if (!key) { setConsent({ status: "missing" }); return; }
    api.get("/medico/patient-consent-v2/current", { params: { patient_key: key } })
      .then(({ data }) => setConsent(data))
      .catch(() => setConsent({ status: "missing" }));
  }, [key]);

  if (!consent) return <Section title="Consensi" icon={ShieldCheck} testid="pp-consent"><div className="text-xs text-slate-400">Caricamento…</div></Section>;

  const signed = consent.status === "signed";
  const sections = consent.sections || {};
  const SECTION_LABELS = { privacy: "Privacy", referto_online: "Referto online", ia_audio: "IA / Audio" };
  return (
    <Section title="Consensi" icon={signed ? ShieldCheck : ShieldAlert} testid="pp-consent">
      {signed ? (
        <div data-testid="pp-consent-signed">
          <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 text-xs font-bold px-2.5 py-1 rounded-full">
            <ShieldCheck size={12} /> Firmato {consent.signed_at ? `il ${new Date(consent.signed_at).toLocaleDateString("it-IT")}` : ""}
          </span>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {Object.entries(SECTION_LABELS).map(([k, label]) => (
              <span key={k} className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${sections[k] ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-600 border border-red-200"}`} data-testid={`pp-consent-section-${k}`}>
                {label}: {sections[k] ? "Sì" : "No"}
              </span>
            ))}
          </div>
        </div>
      ) : (
        <div data-testid="pp-consent-missing">
          <span className="inline-flex items-center gap-1.5 bg-red-100 text-red-700 text-xs font-bold px-2.5 py-1 rounded-full">
            <ShieldAlert size={12} /> Consenso mancante
          </span>
          <p className="text-xs text-slate-500 mt-2">Raccogli il consenso dalla cartella clinica del paziente (blocco Consenso privacy).</p>
        </div>
      )}
    </Section>
  );
}

function NextAppointmentSection({ patient }) {
  const [appt, setAppt] = useState(undefined);
  useEffect(() => {
    api.get("/medico/patient-next-appointment", { params: idParams(patient) })
      .then(({ data }) => setAppt(data?.found ? data.appointment : null))
      .catch(() => setAppt(null));
  }, [patient]);

  return (
    <Section title="Prossimo appuntamento" icon={Calendar} testid="pp-next-appt">
      {appt === undefined ? <div className="text-xs text-slate-400">Caricamento…</div> : appt ? (
        <div className="text-sm" data-testid="pp-next-appt-found">
          <div className="font-bold text-brand-navy">{new Date(appt.starts_at).toLocaleString("it-IT", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}</div>
          <div className="text-xs text-slate-500 mt-0.5">{serviceLabel(appt.service || appt.type)} {appt.site_name ? `· ${appt.site_name}` : ""}</div>
        </div>
      ) : (
        <div className="text-xs text-slate-500" data-testid="pp-next-appt-none">Nessun appuntamento programmato. Programmalo dall'<b>Agenda</b>.</div>
      )}
    </Section>
  );
}

function VisitsSection({ patient }) {
  const [visits, setVisits] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    api.get("/visits/patient-history", { params: idParams(patient) })
      .then(({ data }) => setVisits(data || []))
      .catch(() => setVisits([]));
  }, [patient]);

  return (
    <Section title="Storico visite" icon={FileText} testid="pp-visits">
      {visits === null ? <div className="text-xs text-slate-400">Caricamento…</div> : visits.length === 0 ? (
        <div className="text-xs text-slate-500" data-testid="pp-visits-empty">Nessuna visita registrata.</div>
      ) : (
        <ul className="space-y-2" data-testid="pp-visits-list">
          {visits.map((v) => (
            <li key={v.id} className="border border-[#E2E8F0] rounded-lg p-3 flex items-center gap-3 flex-wrap" data-testid={`pp-visit-${v.id}`}>
              <div className="flex-1 min-w-[160px]">
                <div className="text-sm font-bold text-brand-navy">{new Date(v.created_at).toLocaleDateString("it-IT")} · {serviceLabel(v.service)}</div>
                <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap mt-0.5">
                  {v.sede_snapshot?.name && (
                    <span className="inline-flex items-center gap-1 font-bold" style={{ color: v.sede_snapshot?.color || "#64748B" }}>
                      <MapPin size={10} /> {v.sede_snapshot.name}
                    </span>
                  )}
                  {v.report_sent_at && <span>Referto inviato {new Date(v.report_sent_at).toLocaleDateString("it-IT")}</span>}
                </div>
              </div>
              <StatusBadge v={v} />
              <button data-testid={`pp-open-visit-${v.id}`} onClick={() => nav(`/visite/${v.id}`)} className="text-brand-azure hover:bg-brand-azure/10 px-3 py-1.5 rounded-lg font-bold text-xs">Apri cartella</button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

export default function PatientProfile({ patient, onClose }) {
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const nav = useNavigate();

  const exportRecord = useCallback(() => {
    // window.open SINCRONO prima della chiamata API (Safari popup blocker)
    const w = window.open("", "_blank");
    if (w) w.document.write("<p style='font-family:sans-serif;padding:20px'>Generazione cartella clinica completa in corso…</p>");
    setExporting(true);
    api.get("/medico/patient-record/export", { params: idParams(patient) })
      .then(({ data }) => {
        if (!w) { toast.error("Popup bloccato dal browser: consenti i popup e riprova"); return; }
        w.document.open(); w.document.write(data); w.document.close();
      })
      .catch((e) => {
        if (w) w.close();
        toast.error(apiErr(e, "Errore durante l'export della cartella"));
      })
      .finally(() => setExporting(false));
  }, [patient]);

  const newVisit = useCallback(async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/visits", {
        patient_name: patient.patient_name || "", patient_surname: patient.patient_surname || "",
        patient_birth: patient.patient_birth || "", patient_fiscal_code: patient.patient_fiscal_code || "",
        patient_phone: patient.patient_phone || "", patient_email: patient.patient_email || "",
        patient_address: patient.patient_address || "", patient_city: patient.patient_city || "",
        patient_cap: patient.patient_cap || "", patient_province: patient.patient_province || "",
        patient_notes: patient.patient_notes || "", privacy_contact_consent: !!patient.privacy_contact_consent,
        service: "generale",
      });
      toast.success("Bozza creata");
      nav(`/visite/${data.id}`);
    } catch (e) { toast.error(apiErr(e, "Errore")); setCreating(false); }
  }, [patient, nav]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="patient-profile-overlay">
      <div className="bg-brand-bg w-full max-w-2xl h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="patient-profile-panel">
        <div className="sticky top-0 z-10 bg-white border-b border-[#E2E8F0] px-5 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-lg font-extrabold text-brand-navy truncate" data-testid="pp-name">{patient.patient_surname} {patient.patient_name}</h3>
            <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 mt-0.5">
              {patient.patient_birth && <span data-testid="pp-birth">Nato/a: {patient.patient_birth}</span>}
              {patient.patient_fiscal_code && <span className="font-mono" data-testid="pp-cf">{patient.patient_fiscal_code}</span>}
              {patient.visits_count != null && <span>{patient.visits_count} visit{patient.visits_count === 1 ? "a" : "e"} chius{patient.visits_count === 1 ? "a" : "e"}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
            <button
              data-testid="pp-export-record"
              onClick={exportRecord}
              disabled={exporting}
              title="Scarica cartella clinica completa (tutte le visite, consensi, chirurgia)"
              className="bg-brand-navy hover:bg-brand-navyDark text-white text-xs font-bold px-3 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-60"
            >
              {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Scarica cartella completa
            </button>
            <button
              data-testid="pp-new-visit"
              onClick={newVisit}
              disabled={creating}
              className="bg-brand-orange hover:bg-brand-orangeDark text-white text-xs font-bold px-3 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-60"
            >
              {creating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Nuova visita
            </button>
            <button data-testid="pp-close" onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><XIcon size={18} /></button>
          </div>
        </div>

        <div className="p-4 space-y-3">
          <Section title="Contatti e anagrafica" icon={Phone} testid="pp-contacts">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2 text-slate-700"><Phone size={13} className="text-slate-400" /> {patient.patient_phone || <span className="text-slate-400">—</span>}</div>
              <div className="flex items-center gap-2 text-slate-700 truncate"><Mail size={13} className="text-slate-400 shrink-0" /> <span className="truncate">{patient.patient_email || <span className="text-slate-400">—</span>}</span></div>
              <div className="flex items-center gap-2 text-slate-700 sm:col-span-2">
                <MapPin size={13} className="text-slate-400 shrink-0" />
                {[patient.patient_address, patient.patient_city, patient.patient_cap, patient.patient_province].filter(Boolean).join(", ") || <span className="text-slate-400">—</span>}
              </div>
            </div>
            {patient.patient_notes && (
              <div className="mt-2 text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-lg p-2 flex items-start gap-1.5" data-testid="pp-notes">
                <StickyNote size={12} className="text-amber-600 shrink-0 mt-0.5" /> {patient.patient_notes}
              </div>
            )}
          </Section>

          <TagsSection patient={patient} />
          <ConsentSection patient={patient} />
          <NextAppointmentSection patient={patient} />
          <VisitsSection patient={patient} />
        </div>
      </div>
    </div>
  );
}
